import { isEmpty } from 'lodash';

import { getCustodialQualifiedTuitionPersonalAccountSummaryProperties } from '@/modules/entities/details/accounts/CustodialQualifiedTuitionPersonalAccountDetails.utils';
import { getIndividualPersonalAccountSummaryProperties } from '@/modules/entities/details/accounts/IndividualPersonalAccountDetails.utils';
import { getInsuranceAccountSummaryProperties } from '@/modules/entities/details/accounts/InsuranceAccountDetails.utils';
import { getJointPersonalAccountSummaryProperties } from '@/modules/entities/details/accounts/JointPersonalAccountDetails.utils';
import { getRetirementPersonalAccountSummaryProperties } from '@/modules/entities/details/accounts/RetirementPersonalAccountDetails.utils';
import { BusinessEntitySummaryData } from '@/modules/entities/details/businessEntities/BusinessEntityDetailsCard/BusinessEntityDetails.types';
import { getBusinessEntitySummaryProperties } from '@/modules/entities/details/businessEntities/BusinessEntityDetailsCard/BusinessEntityDetails.utils';
import { EntityDetail_InsurancePolicyFragment } from '@/modules/entities/details/graphql/EntityDetailPage.generated';
import { getGRATTrustSummaryProperties } from '@/modules/entities/details/grat/GRATTrustDetails';
import { getCLTTrustSummaryProperties } from '@/modules/entities/details/trusts/CLTTrustDetails';
import { getCRTTrustSummaryProperties } from '@/modules/entities/details/trusts/CRTTrustDetails';
import { getILITTrustSummaryProperties } from '@/modules/entities/details/trusts/ILITTrustDetails';
import { getQPRTTrustSummaryProperties } from '@/modules/entities/details/trusts/QPRTTrustDetails';
import { getRevocableIrrevocableTrustSummaryProperties } from '@/modules/entities/details/trusts/RevocableIrrevocableTrustDetails';
import { getSLATTrustSummaryProperties } from '@/modules/entities/details/trusts/SLATTrustDetails';
import { ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { NonTrustCharitableEntitySummaryData } from '@/modules/nonTrustCharitableEntities/NonTrustCharitableEntityDetails/NonTrustCharitableEntityDetails.types';
import { getNonTrustCharitableEntitySummaryProperties } from '@/modules/nonTrustCharitableEntities/NonTrustCharitableEntityDetails/NonTrustCharitableEntityDetails.utils';
import { PersonalAccountSummaryData } from '@/modules/personalAccounts/PersonalAccountDetails/PersonalAccountDetails.types';
import { ClientPresentationDesignerV2_EntityFragment } from '@/modules/presentation/clientPresentation/graphql/ClientPresentationDesignerV2.generated';
import { getPaginatedBeneficiariesAndDispositionsForSummary } from '@/modules/presentation/entities/entityPresentationPagination.utils';
import { EntitySummaryGrantor } from '@/modules/presentation/entities/entityPresentations.types';
import { TrustBeneficiariesList_BeneficiaryFragment } from '@/modules/trusts/TrustBeneficiariesList/graphql/TrustBeneficiariesList.fragments.generated';
import { TrustSummaryData } from '@/modules/trusts/TrustDetails/TrustDetails.types';
import { EntityKind } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import { EntityBeneficiariesAndDispositionsSlideProps } from './EntityBeneficiariesAndDispositionsSlide';

export function getInsurancePolicies(
  entity: ClientPresentationDesignerV2_EntityFragment
): EntityDetail_InsurancePolicyFragment[] | null | undefined {
  const summary = getEntitySummary(entity);
  return summary.insurancePolicies;
}

export function getSummaryPropsList({
  entity,
  deathOrderId,
  grantors,
  footer,
  useBeneficiaryDesignations = false,
}: {
  entity: ClientPresentationDesignerV2_EntityFragment;
  deathOrderId?: string | null | undefined;
  grantors: EntitySummaryGrantor[];
  footer?: React.ReactNode;
  /**
   * When true (retirement/DH-insurance under the BDG FF), the empty-row
   * filter is skipped so the slide still renders the BDG primary/contingent
   * table even when no DP rows exist.
   */
  useBeneficiaryDesignations?: boolean;
}): Omit<EntityBeneficiariesAndDispositionsSlideProps, 'slideId'>[] {
  const summary = getEntitySummary(entity);
  let beneficiaries: TrustBeneficiariesList_BeneficiaryFragment[] = [];
  if ('beneficiaries' in summary) {
    beneficiaries = summary.beneficiaries ?? [];
  }

  let beneficiariesGroups:
    | TrustBeneficiariesList_BeneficiaryFragment[][]
    | undefined = undefined;
  if ('beneficiariesGroups' in summary) {
    beneficiariesGroups = summary.beneficiariesGroups.map((bg) => bg.items);
  }

  const dispositionScenarios =
    entity.dispositionScenariosSubtype?.dispositionScenarios ?? [];

  // by default, getPaginatedBeneficiariesAndDispositionsForSummary will show the disposition scenario with the most
  // dispositions, but we want to override this behavior because the user is looking at a specific death order
  const dispositionIdToShow = dispositionScenarios.find(
    (scenario) => scenario.firstGrantorDeath.id === deathOrderId
  )?.id;

  const summaryPagesProps = getPaginatedBeneficiariesAndDispositionsForSummary({
    dispositionScenarios,
    dispositionIdToShow,
    entityId: entity.id,
    slideTypes: [],
    beneficiaries,
    beneficiariesGroups,
    entityKind: entity.kind,
    grantors,
    description: summary.description,
  });

  const baseProps: Pick<
    EntityBeneficiariesAndDispositionsSlideProps,
    'entity' | 'grantors' | 'footer'
  > = {
    entity,
    grantors,
    footer,
  };

  return summaryPagesProps
    .filter((slide) => {
      if (useBeneficiaryDesignations) return true;
      const emptyBeneficiaries = isEmpty(slide.beneficiariesRows);
      const emptyDispositionRows =
        !slide.dispositionRowsByDeath ||
        slide.dispositionRowsByDeath?.every((rows) => isEmpty(rows));

      if (emptyBeneficiaries && emptyDispositionRows) return false;
      return true;
    })
    .map<Omit<EntityBeneficiariesAndDispositionsSlideProps, 'slideId'>>(
      (slideProps) => ({
        ...baseProps,
        ...slideProps,
      })
    );
}

type GetEntitySummaryOutputType =
  | TrustSummaryData
  | PersonalAccountSummaryData
  | NonTrustCharitableEntitySummaryData
  | BusinessEntitySummaryData;

function getEntitySummary(
  entity: ClientPresentationDesignerV2_EntityFragment
): GetEntitySummaryOutputType {
  const entityKind = entity.kind;
  switch (entityKind) {
    case EntityKind.IrrevocableTrust:
    case EntityKind.RevocableTrust:
      return getRevocableIrrevocableTrustSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });

    case EntityKind.SlatTrust:
      return getSLATTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.SLAT,
      });

    case EntityKind.IlitTrust:
      return getILITTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.ILIT,
      });
    case EntityKind.QprtTrust:
      return getQPRTTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.QPRT,
      });

    case EntityKind.CrtTrust:
      return getCRTTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.CRT,
      });

    case EntityKind.CltTrust:
      return getCLTTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.CLT,
      });

    case EntityKind.GratTrust:
      return getGRATTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.GRAT,
      });
    case EntityKind.IndividualPersonalAccount:
      return getIndividualPersonalAccountSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
    case EntityKind.JointPersonalAccount:
      return getJointPersonalAccountSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
    case EntityKind.InsurancePersonalAccount:
      return getInsuranceAccountSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
    case EntityKind.RetirementPersonalAccount:
      return getRetirementPersonalAccountSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
    case EntityKind.QualifiedTuitionPersonalAccount:
    case EntityKind.CustodialPersonalAccount:
      return getCustodialQualifiedTuitionPersonalAccountSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
    case EntityKind.DonorAdvisedFund:
    case EntityKind.PrivateFoundation:
      return getNonTrustCharitableEntitySummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
    case EntityKind.LlcBusinessEntity:
      return getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.LLC,
      });
    case EntityKind.SoleProprietorshipBusinessEntity:
      return getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.SOLE_PROPRIETORSHIP,
      });
    case EntityKind.ScorpBusinessEntity:
      return getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.S_CORP,
      });
    case EntityKind.CcorpBusinessEntity:
      return getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.C_CORP,
      });
    case EntityKind.GpBusinessEntity:
      return getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.GP,
      });
    case EntityKind.LpBusinessEntity:
      return getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.LP,
      });
    default:
      throw new UnreachableError({
        case: entityKind,
        message: `Unhandled business entity kind: ${entityKind}`,
      });
  }
}
